import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Divider,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { useRoute } from '@/hooks/useRoute';
import { useRouteMutations } from '@/hooks/useRouteMutations';
import { useRouteWorkspace } from '@/stores/RouteWorkspaceContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import {
  pickCanonicalVariant,
  type RouteResponse,
  type VariantSummary,
} from '@/types/routes';
import { extractAxiosMessage } from '@/utils/axiosError';

interface RoutePropertiesPanelProps {
  readonly routeId: string;
}

export default function RoutePropertiesPanel({ routeId }: RoutePropertiesPanelProps) {
  const { t } = useTranslation();
  const { modal, notification } = App.useApp();
  const state = useRoute(routeId);
  const { selectRoute } = useRouteWorkspace();
  const { bumpRoutes } = useTileRevision();
  const { saving, remove } = useRouteMutations();

  if (state.status === 'idle' || state.status === 'loading') {
    return <Skeleton active paragraph={{ rows: 5 }} />;
  }
  if (state.status === 'not_found') {
    return (
      <Alert
        type="warning"
        message={t('routes.details.notFound', { id: routeId })}
        showIcon
      />
    );
  }
  if (state.status === 'error') {
    return (
      <Alert
        type="error"
        message={t('routes.details.loadError')}
        description={state.message}
        showIcon
      />
    );
  }

  const route = state.data;
  const canonical = pickCanonicalVariant(route.variants);
  const barrierAware = route.variants.find((v) => v.variant === 'barrier_aware');
  const anyStale = route.variants.some((v) => v.stale);

  const handleDelete = () => {
    modal.confirm({
      title: t('routes.delete.title'),
      content: t('routes.delete.description'),
      okText: t('routes.delete.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove(routeId);
          selectRoute(null);
          bumpRoutes();
        } catch (error) {
          notification.error({
            message: t('routes.delete.errorTitle'),
            description: extractAxiosMessage(error) ?? t('errors.unexpected'),
          });
          throw error;
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {barrierAware && barrierAware.residual_barriers.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={t('routes.residual.title', {
            count: barrierAware.residual_barriers.length,
          })}
          description={t('routes.residual.description')}
        />
      )}

      {anyStale && (
        <Alert
          type="info"
          showIcon
          message={t('routes.stale.title')}
          description={t('routes.stale.description')}
        />
      )}

      <Descriptions column={1} size="small" items={buildOdItems(route, canonical, t)} />

      <Divider style={{ margin: '8px 0' }} />

      <Typography.Text strong>{t('routes.variantsSectionTitle')}</Typography.Text>
      <VariantsBreakdown variants={route.variants} />

      <Space>
        <Button
          danger
          icon={<DeleteOutlined />}
          loading={saving}
          onClick={handleDelete}
        >
          {t('common.delete')}
        </Button>
      </Space>
    </Space>
  );
}

interface VariantsBreakdownProps {
  readonly variants: readonly VariantSummary[];
}

function VariantsBreakdown({ variants }: VariantsBreakdownProps) {
  const { t } = useTranslation();
  if (variants.length === 0) {
    return <Typography.Text type="secondary">{t('routes.variantsEmpty')}</Typography.Text>;
  }
  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {variants.map((variant) => (
        <VariantRow key={variant.variant} variant={variant} />
      ))}
    </Space>
  );
}

function VariantRow({ variant }: { readonly variant: VariantSummary }) {
  const { t } = useTranslation();
  const durationText = variant.duration_min !== null
    ? formatDuration(variant.duration_min, t)
    : '—';
  return (
    <Space
      style={{
        width: '100%',
        justifyContent: 'space-between',
      }}
      size="small"
    >
      <Space size={4}>
        <Tag color={variantTagColor(variant.variant)}>
          {t(`routes.variantShort.${variant.variant}`)}
        </Tag>
        {variant.stale && (
          <Tag color="orange" style={{ margin: 0 }}>
            {t('routes.variantStale')}
          </Tag>
        )}
      </Space>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {variant.length_km.toFixed(2)} {t('common.units.kilometers')} · {durationText}
      </Typography.Text>
    </Space>
  );
}

function buildOdItems(
  route: RouteResponse,
  canonical: VariantSummary | null,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  const origin = route.waypoints[0];
  const destination = route.waypoints[route.waypoints.length - 1];
  const items = [
    {
      key: 'from',
      label: t('routes.fields.origin'),
      children: route.from_address ?? formatLatLon(origin),
    },
    {
      key: 'to',
      label: t('routes.fields.destination'),
      children: route.to_address ?? formatLatLon(destination),
    },
  ];
  if (canonical) {
    items.push(
      {
        key: 'canonical-length',
        label: t('routes.fields.length'),
        children: `${canonical.length_km.toFixed(2)} ${t('common.units.kilometers')}`,
      },
      {
        key: 'canonical-duration',
        label: t('routes.fields.duration'),
        children: canonical.duration_min !== null
          ? formatDuration(canonical.duration_min, t)
          : '—',
      },
    );
  }
  return items;
}

function variantTagColor(variant: VariantSummary['variant']): string {
  switch (variant) {
    case 'gis':
      return 'gold';
    case 'iteko':
      return 'purple';
    case 'barrier_aware':
      return 'green';
  }
}

function formatLatLon(point: { lat: number; lon: number } | undefined): string {
  if (!point) return '—';
  return `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
}

function formatDuration(
  minutes: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} ${t('common.units.minutes')}`;
  return `${hours} ${t('common.units.hoursShort')} ${mins} ${t('common.units.minutes')}`;
}
